// MusicWidget.js

import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import axios from 'axios';
import { withFirebase } from '../firebase/firebasecontext';
import { AuthUserContext } from '../../views/session/context';
import { MusicPlayerContext } from './MusicPlayerContext';
import { initializeMusicKit } from './musickit';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';

import { IconButton } from '@mui/material';
import '../../scss/main.scss'
import {
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPreviousIcon,
  Shuffle as ShuffleIcon, // Ensure ShuffleIcon is imported
} from '@mui/icons-material';



const MusicWidget = ({ firebase, isCollapsed }) => {
  // Access the authenticated user from AuthUserContext
  const { authUser } = useContext(AuthUserContext);
  const {
    selectedPlaylist,
    setSelectedPlaylist,
    playlistTracks,
    setPlaylistTracks,
    currentTrackIndex,
    setCurrentTrackIndex,
    isPlaying,
    setIsPlaying,
    isShuffling,
    setIsShuffling,
    playerRef,
  } = useContext(MusicPlayerContext);
  const [authError, setAuthError] = useState(null);
  const [isFetchingPlaylists, setIsFetchingPlaylists] = useState(false);
  const [musicSource, setMusicSource] = useState(
    () => localStorage.getItem('musicSource') || (import.meta.env.VITE_APPLE_MUSIC_DEVELOPER_TOKEN ? 'apple' : 'youtube')
  );

  const [playlists, setPlaylists] = useState([]);

  const lastAuthUidRef = useRef(null);
  const isPlaylistAvailable = playlistTracks.length > 0;
  const musickitInstance = useRef(null);
  const providerId =
    authUser?.providerData?.[0]?.providerId ||
    authUser?.authUser?.providerData?.[0]?.providerId ||
    null;
  const isAppleAuth = providerId === 'apple.com';
  const appleDeveloperToken = import.meta.env.VITE_APPLE_MUSIC_DEVELOPER_TOKEN;

  const getGoogleToken = () => localStorage.getItem('googleAccessToken');

  const fetchYouTubePlaylists = useCallback(async () => {
    try {
      setIsFetchingPlaylists(true);
      const token = getGoogleToken();
      if (!token) {
        setAuthError(
          isAppleAuth
            ? 'You are signed in with Apple. Private YouTube playlists require Google connection.'
            : 'Connect YouTube to load your playlists.'
        );
        setPlaylists([]);
        return;
      }

      const response = await axios.get('https://www.googleapis.com/youtube/v3/playlists', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { part: 'snippet,contentDetails', mine: true, maxResults: 50 },
      });

      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      setPlaylists(items);
      setMusicSource('youtube');
      setAuthError(null);

      if (!selectedPlaylist && items.length > 0) {
        const initialPlaylist = items[0].id;
        setSelectedPlaylist(initialPlaylist);
        localStorage.setItem('selectedPlaylist', initialPlaylist);
      }
    } catch (error) {
      console.error('Playlist fetch error:', error);
      setPlaylists([]);
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setAuthError('YouTube session expired. Reconnect to refresh access.');
      } else {
        setAuthError('Unable to load playlists right now.');
      }
    } finally {
      setIsFetchingPlaylists(false);
    }
  }, [isAppleAuth, selectedPlaylist, setSelectedPlaylist]);

  const fetchAppleMusicPlaylists = useCallback(async () => {
    try {
      if (!appleDeveloperToken) return false;
      const music = await initializeMusicKit(appleDeveloperToken);
      musickitInstance.current = music;

      if (!music.isAuthorized) {
        const savedUserToken = localStorage.getItem('appleMusicUserToken');
        if (savedUserToken) {
          music.musicUserToken = savedUserToken;
        }
      }

      if (!music.isAuthorized) {
        setAuthError('Apple Music available. Tap Connect Apple Music to authorize.');
        return false;
      }

      const response = await music.api.library.playlists();
      const items = Array.isArray(response?.data) ? response.data : [];
      const mapped = items.map((item) => ({
        id: item.id,
        snippet: { title: item.attributes?.name || 'Untitled Playlist' },
        raw: item,
        source: 'apple',
      }));
      setPlaylists(mapped);
      setMusicSource('apple');
      setAuthError(null);
      if (!selectedPlaylist && mapped.length > 0) {
        const initialPlaylist = mapped[0].id;
        setSelectedPlaylist(initialPlaylist);
        localStorage.setItem('selectedPlaylist', initialPlaylist);
      }
      return true;
    } catch (error) {
      console.error('Apple Music playlist fetch error:', error);
      return false;
    }
  }, [appleDeveloperToken, selectedPlaylist, setSelectedPlaylist]);

  const handleAppleMusicConnect = useCallback(async () => {
    try {
      if (!appleDeveloperToken) {
        setAuthError('Apple Music not configured. Set VITE_APPLE_MUSIC_DEVELOPER_TOKEN.');
        return;
      }
      const music = await initializeMusicKit(appleDeveloperToken);
      musickitInstance.current = music;
      if (!music.isAuthorized) {
        const token = await music.authorize();
        if (token) localStorage.setItem('appleMusicUserToken', token);
      }
      const ok = await fetchAppleMusicPlaylists();
      if (!ok) {
        await fetchYouTubePlaylists();
      }
    } catch (error) {
      console.error('Apple Music connect error:', error);
      setAuthError('Apple Music authorization failed. Falling back to YouTube.');
      await fetchYouTubePlaylists();
    }
  }, [appleDeveloperToken, fetchAppleMusicPlaylists, fetchYouTubePlaylists]);

  useEffect(() => {
    localStorage.setItem('musicSource', musicSource);
  }, [musicSource]);

  useEffect(() => {
    const uid = authUser?.uid || authUser?.authUser?.uid || null;
    if (uid && uid !== lastAuthUidRef.current) {
      lastAuthUidRef.current = uid;
      (async () => {
        const appleOk = await fetchAppleMusicPlaylists();
        if (!appleOk) await fetchYouTubePlaylists();
      })();
    }
  }, [authUser?.uid, authUser?.authUser?.uid, fetchAppleMusicPlaylists, fetchYouTubePlaylists]);

  // Fetch tracks when selectedPlaylist changes
  useEffect(() => {
    if (selectedPlaylist && authUser) {
      fetchPlaylistTracks(selectedPlaylist);
    }
  }, [selectedPlaylist, authUser]);

  // Add this useEffect for handling visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Page is in background
        if (playerRef.current && isPlaying) {
          // Keep playing, but we might need to handle the state
          console.log('Background playback continuing...');
        }
      } else {
        // Page is visible again
        if (playerRef.current && isPlaying) {
          // Check if playback actually stopped and resume if needed
          const playerState = playerRef.current.getPlayerState();
          if (playerState !== window.YT.PlayerState.PLAYING) {
            playerRef.current.playVideo();
          }
        }
      }
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying]);

  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => {
        if (playerRef.current) {
          playerRef.current.playVideo();
          setIsPlaying(true);
        }
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        if (playerRef.current) {
          playerRef.current.pauseVideo();
          setIsPlaying(false);
        }
      });

      navigator.mediaSession.setActionHandler('nexttrack', () => {
        playNextTrack();
      });

      navigator.mediaSession.setActionHandler('previoustrack', () => {
        playPreviousTrack();
      });
    }
  }, []);

  if (!authUser) {
    console.log('music widget is not authenticated');
    return (
      <div className="music-widget">
        <p>Please sign in to access music</p>
      </div>
    );
  }

  const getActiveProvider = () => musicSource;

  const handleReauth = async () => {
    try {
      if (isAppleAuth) {
        await handleAppleMusicConnect();
      } else {
        if (typeof firebase?.doSignInWithGoogle === 'function') {
          await firebase.doSignInWithGoogle();
        } else {
          const provider = new GoogleAuthProvider();
          provider.addScope('https://www.googleapis.com/auth/youtube.readonly');
          provider.addScope('https://www.googleapis.com/auth/youtube.force-ssl');
          const result = await signInWithPopup(getAuth(), provider);
          const credential = GoogleAuthProvider.credentialFromResult(result);
          if (credential?.accessToken) {
            localStorage.setItem('googleAccessToken', credential.accessToken);
          }
        }
        await fetchYouTubePlaylists();
      }
    } catch (error) {
      console.error('Reauth error:', error);
      setAuthError('Authentication failed. Please try again.');
    }
  };

  // Fetch all tracks from the selected playlist, handling pagination with Bearer token
  const fetchPlaylistTracks = async (playlistId) => {
    const activeProvider = getActiveProvider();
    let allTracks = [];
    let nextPageToken = null;

    try {
      if (activeProvider === 'youtube') {
        do {
          const response = await axios.get(`https://www.googleapis.com/youtube/v3/playlistItems`, {
            headers: {
              Authorization: `Bearer ${getGoogleToken()}`,
            },
            params: {
              part: 'snippet',
              playlistId: playlistId,
              maxResults: 50,
              pageToken: nextPageToken || '',
            },
          });
          const tracks = response.data.items;
          allTracks = [...allTracks, ...tracks];
          nextPageToken = response.data.nextPageToken;
        } while (nextPageToken);

        setPlaylistTracks(allTracks);
      } else if (activeProvider === 'apple') {
        if (!musickitInstance.current) throw new Error('Apple Music not initialized');
        const response = await musickitInstance.current.api.library.playlist(playlistId);
        allTracks = response.relationships.tracks.data.map((track) => ({
          snippet: {
            title: track.attributes.name,
            resourceId: { videoId: null },
          },
        }));
        setPlaylistTracks(allTracks);

        try {
          const selected = playlists.find((p) => p.id === playlistId);
          const playParams = selected?.raw?.attributes?.playParams;
          if (playParams?.id) {
            const kind = playParams.kind || 'playlist';
            await musickitInstance.current.setQueue({ [kind]: playParams.id });
          }
        } catch (queueError) {
          console.warn('Apple Music queue setup failed:', queueError);
        }
      }

      // Preserve track index across route changes/refetches for the same playlist.
      const savedPlaylist = localStorage.getItem('selectedPlaylist');
      const savedIndex = Number(localStorage.getItem('currentTrackIndex'));
      const canReuseSavedIndex =
        savedPlaylist === playlistId &&
        Number.isInteger(savedIndex) &&
        savedIndex >= 0 &&
        savedIndex < allTracks.length;

      if (canReuseSavedIndex) {
        setCurrentTrackIndex(savedIndex);
      } else {
        const initialIndex = 0;
        setCurrentTrackIndex(initialIndex);
        localStorage.setItem('currentTrackIndex', initialIndex);
      }
    } catch (error) {
      console.error('Error fetching playlist tracks:', error);
    }
  };

  // Handle playlist selection
  const handlePlaylistClick = (playlistId) => {
    setSelectedPlaylist(playlistId);
    localStorage.setItem('selectedPlaylist', playlistId);
    // Fetch the tracks for the selected playlist
    fetchPlaylistTracks(playlistId);
  };

  const handlePlayPause = () => {
    if (musicSource === 'apple' && musickitInstance.current) {
      if (isPlaying) {
        musickitInstance.current.pause();
      } else {
        musickitInstance.current.play();
      }
      setIsPlaying(!isPlaying);
      localStorage.setItem('isPlaying', String(!isPlaying));
      return;
    }
    if (playerRef.current) {
      if (isPlaying) {
        playerRef.current.pauseVideo();
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'paused';
        }
      } else {
        playerRef.current.playVideo();
        if ('mediaSession' in navigator) {
          navigator.mediaSession.playbackState = 'playing';
        }
      }
      setIsPlaying(!isPlaying);
      localStorage.setItem('isPlaying', !isPlaying);
    }
  };

  const playNextTrack = () => {
    if (musicSource === 'apple' && musickitInstance.current) {
      musickitInstance.current.skipToNextItem();
      setIsPlaying(true);
      localStorage.setItem('isPlaying', 'true');
      return;
    }
    const newIndex = isShuffling
      ? Math.floor(Math.random() * playlistTracks.length)
      : (currentTrackIndex + 1) % playlistTracks.length;
    setCurrentTrackIndex(newIndex);
    localStorage.setItem('currentTrackIndex', newIndex);
    setIsPlaying(true);
    localStorage.setItem('isPlaying', 'true');
  };

  const playPreviousTrack = () => {
    if (musicSource === 'apple' && musickitInstance.current) {
      musickitInstance.current.skipToPreviousItem();
      setIsPlaying(true);
      localStorage.setItem('isPlaying', 'true');
      return;
    }
    const newIndex = isShuffling
      ? Math.floor(Math.random() * playlistTracks.length)
      : (currentTrackIndex - 1 + playlistTracks.length) % playlistTracks.length;
    setCurrentTrackIndex(newIndex);
    localStorage.setItem('currentTrackIndex', newIndex);
    setIsPlaying(true);
    localStorage.setItem('isPlaying', 'true');
  };

  useEffect(() => {
    const onAppleControl = (event) => {
      if (musicSource !== 'apple') return;
      const action = event?.detail?.action;
      if (action === 'toggle') handlePlayPause();
      if (action === 'next') playNextTrack();
      if (action === 'previous') playPreviousTrack();
      if (action === 'stop' && musickitInstance.current) {
        musickitInstance.current.pause();
        setIsPlaying(false);
        localStorage.setItem('isPlaying', 'false');
      }
      if (action === 'shuffle') {
        const newShuffleState = !isShuffling;
        setIsShuffling(newShuffleState);
        localStorage.setItem('isShuffling', String(newShuffleState));
      }
    };

    window.addEventListener('mie:music-control-apple', onAppleControl);
    return () => window.removeEventListener('mie:music-control-apple', onAppleControl);
  }, [musicSource, isShuffling, setIsPlaying, setIsShuffling]);

  const renderPlayerControls = () => (
    <>

      {isCollapsed ? (
        <div className="music-controls music-controls-collapsed">
          <IconButton onClick={playPreviousTrack} disabled={!isPlaylistAvailable}>
            <SkipPreviousIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={playNextTrack} disabled={!isPlaylistAvailable}>
            <SkipNextIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={handlePlayPause}
            disabled={!isPlaylistAvailable}
          >
            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
          <IconButton
            onClick={() => {
              const newShuffleState = !isShuffling;
              setIsShuffling(newShuffleState);
              localStorage.setItem('isShuffling', newShuffleState);
            }}
            disabled={!isPlaylistAvailable}
          >
            <ShuffleIcon color={isShuffling ? 'primary' : 'inherit'} fontSize="small" />
          </IconButton>
        </div>
      ) : (
        <div className="music-controls music-controls-expanded">
          <div className="music-controls-row">
            <IconButton onClick={playPreviousTrack} disabled={!isPlaylistAvailable}>
              <SkipPreviousIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={playNextTrack} disabled={!isPlaylistAvailable}>
              <SkipNextIcon fontSize="small" />
            </IconButton>
          </div>
          <div className="music-controls-row">
            <IconButton
              onClick={handlePlayPause}
              disabled={!isPlaylistAvailable}
            >
              {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
            <IconButton
              onClick={() => {
                const newShuffleState = !isShuffling;
                setIsShuffling(newShuffleState);
                localStorage.setItem('isShuffling', newShuffleState);
              }}
              disabled={!isPlaylistAvailable}
            >
              <ShuffleIcon color={isShuffling ? 'primary' : 'inherit'} fontSize="small" />
            </IconButton>
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className={`music-widget ${isCollapsed ? 'collapsed' : 'expanded'}`}>
      <div className="music-widget-header">
        <div className="music-widget-title">Music</div>
        <div className="music-widget-subtitle">
          {isFetchingPlaylists ? 'Loading playlists...' : `${playlists.length} playlists · ${musicSource === 'apple' ? 'Apple Music' : 'YouTube'}`}
        </div>
      </div>
      {authError ? (
        <div className="auth-error">
          <p>{authError}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {appleDeveloperToken && (
              <button className="music-auth-btn" onClick={handleAppleMusicConnect}>
                Connect Apple Music
              </button>
            )}
            <button className="music-auth-btn" onClick={handleReauth}>
              Connect YouTube
            </button>
          </div>
        </div>
      ) : (
        <>
          {renderPlayerControls()}

          <ul className="playlist-list">
            {playlists.map((playlist) => (
              <li key={playlist.id}>
                <button
                  className={`playlist-button ${selectedPlaylist === playlist.id ? 'active' : ''
                    }`}
                  onClick={() => handlePlaylistClick(playlist.id)}
                  aria-pressed={selectedPlaylist === playlist.id}
                >
                  <PlaylistPlayIcon />
                  <span className="playlist-title">{playlist.snippet.title}</span>
                </button>
              </li>
            ))}
          </ul>

          {(!isFetchingPlaylists && playlists.length === 0) && (
            <div className="music-empty">
              {musicSource === 'apple'
                ? 'No Apple Music library playlists found for this account.'
                : 'No YouTube playlists found for this account.'}
            </div>
          )}

          {isPlaylistAvailable && (
            <div className="now-playing-text">
              <span>Now playing: {playlistTracks[currentTrackIndex]?.snippet?.title}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default withFirebase(MusicWidget);
